/**
 * System Settings API
 *
 * Client-side helpers for reading/writing system settings.
 * All settings are stored in a single KV entry and admin-only.
 */

import { apiFetch } from "./apiClient";

/* ================================================================
   Types
   ================================================================ */

export interface SystemSettings {
  /* ─── General ─── */
  companyName?: string;
  companyNameEn?: string;
  logoUrl?: string;
  shareDomain?: string;
  supportEmail?: string;
  supportPhone?: string;
  maintenanceMode?: boolean;
  maintenanceMessage?: string;

  /* ─── Email ─── */
  emailEnabled?: boolean;
  emailSenderLabel?: string;
  emailFooterText?: string;
  emailFooterTextEn?: string;
  emailHeaderColor?: string;
  emailAccentColor?: string;

  /* ─── Client Email (separate style) ─── */
  clientEmailHeaderColor?: string;
  clientEmailAccentColor?: string;
  clientEmailFooterText?: string;
  clientEmailFooterTextEn?: string;
  clientEmailButtonColor?: string;
  clientEmailButtonText?: string;

  /* ─── AI ─── */
  aiEnabled?: boolean;
  aiModel?: string;
  aiTemperature?: number;
  aiDefaultTone?: string;
  aiDefaultLanguage?: string;
  aiCustomInstructions?: string;
  aiMaxTokens?: number;

  /* ─── Templates ─── */
  defaultTemplateType?: "branding" | "store" | "facelift";
  requireTags?: boolean;
  autoSnapshotOnStatusChange?: boolean;
  defaultStatus?: string;
  lockAfterApproval?: boolean;
  requireCommentOnStatusChange?: boolean;
  autoAssignReviewer?: string;
  autoSaveIntervalSeconds?: number;
  shareLinkExpiryDays?: number;

  /* ─── Security ─── */
  sessionTimeoutHours?: number;
  minPasswordLength?: number;
  maxLoginAttempts?: number;
  requirePasswordUppercase?: boolean;
  requirePasswordNumber?: boolean;
  autoLockMinutes?: number;

  /* ─── Notifications ─── */
  notificationsEnabled?: boolean;
  emailNotificationsEnabled?: boolean;
  notifyOnComment?: boolean;
  notifyOnMention?: boolean;
  notifyOnStatusChange?: boolean;
  notifyOnAssignment?: boolean;
  quietHoursEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;

  /* ─── Appearance ─── */
  defaultTheme?: "light" | "dark";
  compactMode?: boolean;
  dashboardDefaultView?: "grid" | "list";
  dashboardDefaultSort?: string;

  /* ─── Theme Editor ─── */
  themePrimaryColor?: string;
  themeAccentColor?: string;
  themeBackgroundColor?: string;
  themeForegroundColor?: string;
  themeCardColor?: string;
  themeDestructiveColor?: string;
  themeFontFamily?: string;
  themeBorderRadius?: number;
  themeFontSize?: number;
  themeSidebarColor?: string;

  /* ─── Meta ─── */
  updatedAt?: number;
}

/** Default settings (match current system behavior) */
export const DEFAULT_SETTINGS: SystemSettings = {
  companyName: "\u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4",
  companyNameEn: "Zap Group",
  logoUrl: "",
  shareDomain: "",
  supportEmail: "",
  supportPhone: "",
  maintenanceMode: false,
  maintenanceMessage: "\u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05D1\u05EA\u05D7\u05D6\u05D5\u05E7\u05D4. \u05E0\u05D7\u05D6\u05D5\u05E8 \u05D1\u05E7\u05E8\u05D5\u05D1.",

  emailEnabled: true,
  emailSenderLabel: "Zap Group",
  emailFooterText: "\u05D4\u05D5\u05D3\u05E2\u05D4 \u05D6\u05D5 \u05E0\u05E9\u05DC\u05D7\u05D4 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05DE\u05DE\u05E2\u05E8\u05DB\u05EA \u05E0\u05D9\u05D4\u05D5\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05E9\u05DC \u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4",
  emailFooterTextEn: "",
  emailHeaderColor: "#001C32",
  emailAccentColor: "#3a6bc5",

  clientEmailHeaderColor: "#001C32",
  clientEmailAccentColor: "#3a6bc5",
  clientEmailFooterText: "\u05D4\u05D5\u05D3\u05E2\u05D4 \u05D6\u05D5 \u05E0\u05E9\u05DC\u05D7\u05D4 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05DE\u05DE\u05E2\u05E8\u05DB\u05EA \u05E0\u05D9\u05D4\u05D5\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05E9\u05DC \u05D6\u05D0\u05E4 \u05D2\u05E8\u05D5\u05E4",
  clientEmailFooterTextEn: "",
  clientEmailButtonColor: "#001C32",
  clientEmailButtonText: "Zap Group",

  aiEnabled: true,
  aiModel: "claude-sonnet",
  aiTemperature: 0.7,
  aiDefaultTone: "\u05E9\u05D9\u05D5\u05D5\u05E7\u05D9",
  aiDefaultLanguage: "he",
  aiCustomInstructions: "",
  aiMaxTokens: 500,

  defaultTemplateType: "branding",
  requireTags: false,
  autoSnapshotOnStatusChange: false,
  defaultStatus: "draft",
  lockAfterApproval: false,
  requireCommentOnStatusChange: false,
  autoAssignReviewer: "",
  autoSaveIntervalSeconds: 0,
  shareLinkExpiryDays: 0,

  sessionTimeoutHours: 2,
  minPasswordLength: 4,
  maxLoginAttempts: 0,
  requirePasswordUppercase: false,
  requirePasswordNumber: false,
  autoLockMinutes: 0,

  notificationsEnabled: true,
  emailNotificationsEnabled: true,
  notifyOnComment: true,
  notifyOnMention: true,
  notifyOnStatusChange: true,
  notifyOnAssignment: true,
  quietHoursEnabled: false,
  quietHoursStart: "22:00",
  quietHoursEnd: "08:00",

  defaultTheme: "light",
  compactMode: false,
  dashboardDefaultView: "grid",
  dashboardDefaultSort: "updatedAt-desc",

  /* Theme defaults match current CSS vars */
  themePrimaryColor: "#052b7a",
  themeAccentColor: "#e8edf8",
  themeBackgroundColor: "#ffffff",
  themeForegroundColor: "#1a1a2e",
  themeCardColor: "#ffffff",
  themeDestructiveColor: "#d4183d",
  themeFontFamily: "Heebo",
  themeBorderRadius: 10,
  themeFontSize: 16,
  themeSidebarColor: "#f8f8f8",
};

/* ================================================================
   Font options for Theme Editor
   ================================================================ */

export const FONT_OPTIONS: { value: string; label: string; fallback: string }[] = [
  { value: "Heebo", label: "Heebo (\u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC)", fallback: "sans-serif" },
  { value: "Assistant", label: "Assistant", fallback: "sans-serif" },
  { value: "Rubik", label: "Rubik", fallback: "sans-serif" },
  { value: "Open Sans", label: "Open Sans", fallback: "sans-serif" },
  { value: "Noto Sans Hebrew", label: "Noto Sans Hebrew", fallback: "sans-serif" },
  { value: "Frank Ruhl Libre", label: "Frank Ruhl Libre (\u05E1\u05E8\u05D9\u05E4\u05D9)", fallback: "serif" },
  { value: "Inter", label: "Inter (Latin)", fallback: "sans-serif" },
  { value: "Poppins", label: "Poppins (Latin)", fallback: "sans-serif" },
];

/* ================================================================
   API Functions
   ================================================================ */

/** Load all settings from server. Returns merged with defaults. */
export async function getSettings(): Promise<SystemSettings> {
  const data = await apiFetch("/settings");
  return { ...DEFAULT_SETTINGS, ...(data.settings || {}) };
}

/** Save partial settings to server. Returns the merged result. */
export async function saveSettings(partial: Partial<SystemSettings>): Promise<SystemSettings> {
  const data = await apiFetch("/settings", {
    method: "PUT",
    body: JSON.stringify({ settings: partial }),
  });
  return { ...DEFAULT_SETTINGS, ...(data.settings || {}) };
}